import React from 'react';
import {
  ChartBarIcon,
  CurrencyPoundIcon,
  UserGroupIcon,
  ClockIcon,
  CalendarIcon,
  MapPinIcon,
  EyeIcon,
  ComputerDesktopIcon,
  DevicePhoneMobileIcon,
  ArrowRightIcon,
  LightBulbIcon,
  ArrowUpIcon,
  UsersIcon,
  BuildingOfficeIcon,
  PresentationChartBarIcon,
  CloudIcon,
    CheckCircleIcon,
} from '@heroicons/react/24/outline';

export default function StatsHelp() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50">
      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Header Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-orange-500 to-amber-600 rounded-full mb-6">
            <ChartBarIcon className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Dashboard Overview Guide</h1>
          <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto">
            Your command center for real-time business insights, staff monitoring, and operational intelligence
          </p>
        </div>

        {/* Overview Card */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-8 border border-gray-100">
          <div className="flex items-start space-x-2 md:space-x-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <LightBulbIcon className="w-6 h-6 text-orange-600" />
              </div>
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">What is the Dashboard?</h2>
              <p className="text-gray-600 text-base md:text-lg leading-relaxed">
                This is your business intelligence hub - a comprehensive dashboard that provides real-time visibility into your operations. Track staff attendance, monitor budget performance, view project status, and get insights into team productivity all in one centralized location. It's designed to give managers the critical information they need to make informed decisions quickly.
              </p>
            </div>
          </div>
        </div>

        {/* Dashboard Components Diagram */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-8 border border-gray-100">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 text-center">Dashboard Components Overview</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Component 1 */}
            <div className="text-center p-4 bg-orange-50 rounded-lg border border-orange-200">
              <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <BuildingOfficeIcon className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Company Header</h3>
              <p className="text-gray-600 text-sm">Company branding, location, and user information display</p>
            </div>

            {/* Component 2 */}
            <div className="text-center p-4 bg-orange-50 rounded-lg border border-orange-200">
              <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <ChartBarIcon className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Key Metrics</h3>
              <p className="text-gray-600 text-sm">Staff attendance, shifts, absences, and budget tracking</p>
            </div>

            {/* Component 3 */}
            <div className="text-center p-4 bg-orange-50 rounded-lg border border-orange-200">
              <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <PresentationChartBarIcon className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Budget Analytics</h3>
              <p className="text-gray-600 text-sm">Visual budget usage tracking with pie chart representation</p>
            </div>

            {/* Component 4 */}
            <div className="text-center p-4 bg-orange-50 rounded-lg border border-orange-200">
              <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <UsersIcon className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Staff Status</h3>
              <p className="text-gray-600 text-sm">Real-time staff clock-in status and online presence</p>
            </div>

            {/* Component 5 */}
            <div className="text-center p-4 bg-orange-50 rounded-lg border border-orange-200">
              <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <ArrowUpIcon className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Project Tracking</h3>
              <p className="text-gray-600 text-sm">Active projects overview with quick navigation</p>
            </div>

            {/* Component 6 */}
            <div className="text-center p-4 bg-orange-50 rounded-lg border border-orange-200">
              <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <CloudIcon className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Analytics</h3>
              <p className="text-gray-600 text-sm">Holiday patterns, task completion, and visitor insights</p>
            </div>
          </div>
        </div>

        {/* Key Metrics Explanation */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-8 border border-gray-100">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 text-center">Understanding Key Metrics</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Staff Metrics */}
            <div className="border border-gray-200 rounded-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <UserGroupIcon className="w-5 h-5 text-orange-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Staff & Attendance</h3>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Staff Clocked In:</span>
                  <span className="font-semibold text-orange-600">Current active staff count</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Shifts Today:</span>
                  <span className="font-semibold text-orange-600">Total scheduled shifts</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Sick Today:</span>
                  <span className="font-semibold text-orange-600">Staff marked as sick</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Holiday Today:</span>
                  <span className="font-semibold text-orange-600">Staff on annual leave</span>
                </div>
              </div>
            </div>

            {/* Financial Metrics */}
            <div className="border border-gray-200 rounded-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <CurrencyPoundIcon className="w-5 h-5 text-orange-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Financial Tracking</h3>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Live Wages Today:</span>
                  <span className="font-semibold text-orange-600">Current vs. budgeted cost</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Today's Budget:</span>
                  <span className="font-semibold text-orange-600">Allocated daily budget</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Budget Usage:</span>
                  <span className="font-semibold text-orange-600">Percentage consumed</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Budget Visualization Guide */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-8 border border-gray-100">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 text-center">Budget Analytics Deep Dive</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Pie Chart Explanation */}
            <div className="border border-gray-200 rounded-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <ChartBarIcon className="w-5 h-5 text-orange-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Budget Pie Chart</h3>
              </div>
              <p className="text-gray-600 text-sm mb-4">
                The interactive pie chart shows your budget utilization at a glance. Purple represents spent budget, blue represents remaining budget.
              </p>
              <div className="bg-purple-50 border border-purple-200 rounded p-3 mb-3">
                <p className="text-purple-800 text-sm"><strong>Purple Segment:</strong> Money already spent on labor costs</p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded p-3">
                <p className="text-blue-800 text-sm"><strong>Blue Segment:</strong> Remaining budget available</p>
              </div>
            </div>

            {/* Budget Calculations */}
            <div className="border border-gray-200 rounded-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <ArrowUpIcon className="w-5 h-5 text-orange-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">How Budget is Calculated</h3>
              </div>
              <div className="space-y-3 text-sm text-gray-600">
                <p>• <strong>Monthly Budget:</strong> Your total monthly labor allocation</p>
                <p>• <strong>Operating Days:</strong> Days per week your business operates</p>
                <p>• <strong>Daily Budget:</strong> Monthly budget ÷ (operating days × 4 weeks)</p>
                <p>• <strong>Live Cost:</strong> Real-time calculation based on hours worked × hourly rates</p>
                <p>• <strong>Percentage Used:</strong> (Live cost ÷ Daily budget) × 100</p>
              </div>
            </div>
          </div>
        </div>

        {/* Staff Status & Projects */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-8 border border-gray-100">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 text-center">Staff Monitoring & Project Management</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Online Status */}
            <div className="border border-gray-200 rounded-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <EyeIcon className="w-5 h-5 text-orange-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Staff Online Status</h3>
              </div>
              <p className="text-gray-600 text-sm mb-4">
                Real-time visibility into which staff members are currently active and available. Shows clock-in times and current status.
              </p>
              <div className="bg-green-50 border border-green-200 rounded p-3">
                <p className="text-green-800 text-sm">Updates automatically every minute to ensure accuracy</p>
              </div>
            </div>

            {/* Active Projects */}
            <div className="border border-gray-200 rounded-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <BuildingOfficeIcon className="w-5 h-5 text-orange-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Active Projects</h3>
              </div>
              <p className="text-gray-600 text-sm mb-4">
                Quick access to current projects with direct navigation to detailed project management views.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded p-3">
                <p className="text-blue-800 text-sm">Click on any project to view detailed progress and management options</p>
              </div>
            </div>
          </div>
        </div>

        {/* Analytics & Insights */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-8 border border-gray-100">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 text-center">Analytics & Business Intelligence</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-4 bg-orange-50 rounded-lg border border-orange-200">
              <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <CalendarIcon className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Holiday Patterns</h3>
              <p className="text-sm text-gray-600">Track holiday usage trends and plan staffing coverage</p>
            </div>

            <div className="text-center p-4 bg-orange-50 rounded-lg border border-orange-200">
              <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <ClockIcon className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Task Analytics</h3>
              <p className="text-sm text-gray-600">Monitor task completion rates and productivity metrics</p>
            </div>

            <div className="text-center p-4 bg-orange-50 rounded-lg border border-orange-200">
              <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <UsersIcon className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Visitor Insights</h3>
              <p className="text-sm text-gray-600">Track visitor patterns and engagement metrics</p>
            </div>
          </div>
        </div>

        {/* Tips Section */}
        <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-2xl p-6 md:p-8 border border-orange-100">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <LightBulbIcon className="w-6 h-6 text-orange-600" />
              </div>
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">Dashboard Best Practices</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-sm">Check the dashboard first thing in the morning to understand staffing levels and budget status</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-sm">Monitor budget usage throughout the day to avoid overspending</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-sm">Use the staff online status to quickly identify who is available for urgent tasks</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-sm">Review holiday and sick patterns to plan adequate staffing coverage</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-sm">Click on active projects to get detailed progress updates and task assignments</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-sm">Use the recalibrate button if you see clock-ins from previous days</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-sm">Monitor visitor patterns to understand peak times and customer flow</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-sm">Review task completion analytics to identify productivity trends and bottlenecks</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-sm">Set up alerts for when budget usage exceeds certain thresholds</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-sm">Use the dashboard data to inform staffing decisions and resource allocation</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}